import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { BehaviorSubject, combineLatest, Observable, ReplaySubject } from 'rxjs';
import { ProtocolEntryDto, ProtocolType } from '@interfaces/interfaces';
import { map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { formatRelative } from 'date-fns';
import { TranslateService } from '@ngx-translate/core';
import { isNotNil } from 'ng-zorro-antd/core/util';
import { participantPages } from '@app/pages/participant/participants.routes';
import { ActiveParticipantService } from '@app/services/participants/active-participant.service';
import { Router } from '@angular/router';
import { WorkflowItemNavigationService } from '@workflow/services/workflow-item-navigation.service';
import { WorkflowService } from '@workflow/services/workflow.service';
import { firstValueFrom } from '@app/util/rxjs-7-compat';

type FieldDescriptor = 'participant' | 'workflow-item';

interface ProtocolFieldViewModel {
	label: string;
	value: string;
	descriptor: FieldDescriptor;
	clickHandler?: () => void;
}

interface ProtocolEntryViewModel {
	header: string;
	timestamp: string;
	fields: ProtocolFieldViewModel[];
}

const HEADER_TRANSLATIONS: Record<ProtocolType, string> = {
	'participant-create': 'timeline.participant-create',
	'participant-update': 'timeline.participant-update',
	'participant-delete': 'timeline.participant-delete',
	'workflow-item-inprogress': 'timeline.workflow-item-inprogress',
	'workflow-item-completed': 'timeline.workflow-item-completed',
	'workflow-item-inprogress-reset': 'timeline.workflow-item-inprogress-reset',
	'workflow-item-completed-reset': 'timeline.workflow-item-completed-reset',
	'config-archive-upload': 'timeline.config-archive-upload',
	'custom-state-change': 'timeline.custom-state-change',
};

type FieldRenderer = (entry: ProtocolEntryDto) => ProtocolFieldViewModel | null;

@Component({
	selector: 'app-timeline',
	templateUrl: './timeline.component.html',
	styleUrls: ['./timeline.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class TimelineComponent {
	public readonly entries$: Observable<ProtocolEntryViewModel[]>;
	
	@Input()
	public set entries(entries: ProtocolEntryDto[]) {
		this.entriesSubject.next(entries.sort((a, b) => b.timestamp - a.timestamp));
	}
	
	private readonly entriesSubject = new ReplaySubject<ProtocolEntryDto[]>(1);
	
	@Input()
	public set visibleFields(fields: FieldDescriptor[]) {
		this.fieldsSubject.next(fields);
	}
	
	private readonly fieldsSubject = new BehaviorSubject<FieldDescriptor[]>(['participant', 'workflow-item']);
	
	constructor(
		private readonly translateService: TranslateService,
		private router: Router,
		private activeParticipantService: ActiveParticipantService,
		private workflowService: WorkflowService,
		private workflowItemNavigationService: WorkflowItemNavigationService,
	) {
		this.entries$ = this.entriesSubject.pipe(
			switchMap(entries => combineLatest(entries.map(this.renderEntry())))
		);
	}
	
	private formatTimestamp(): Observable<(timestamp: number) => string> {
		const now = new Date();
		
		return this.translateService.onLangChange.pipe(
			map(e => e.lang),
			startWith(this.translateService.currentLang),
			switchMap(lang => {
				switch (lang?.slice(0, 2)) {
					case 'en':
						return import('date-fns/locale/en-GB');
					case 'de':
					default:
						return import('date-fns/locale/de');
				}
			}),
			map(locale => timestamp => formatRelative(timestamp, now, {
				weekStartsOn: 1,
				locale: locale.default,
			})),
		);
	}
	
	private renderEntry(): (entry: ProtocolEntryDto) => Observable<ProtocolEntryViewModel> {
		const renderFns$ = this.fieldsSubject.pipe(
			map(descriptors => descriptors.map(this.renderField)),
		);
		
		const context$ = combineLatest([
			renderFns$,
			this.formatTimestamp(),
		]).pipe(
			shareReplay({bufferSize: 1, refCount: true})
		);
		
		return (entry) => {
			return context$.pipe(
				map(([renderFields, formatTimestamp]) => ({
					header: this.translateService.instant(HEADER_TRANSLATIONS[entry.type], {
						username: entry.username,
						workflowItem: entry.translation?.item ? `"${entry.translation.item}"` : this.translateService.instant('timeline.workflow-item-unknown'),
						participant: entry.translation?.participant ?? this.translateService.instant('timeline.participant-unknown')
					}),
					timestamp: formatTimestamp(entry.timestamp),
					fields: renderFields.map(fn => fn(entry)).filter(isNotNil),
				})),
			);
		};
	}
	
	private readonly renderField: (field: FieldDescriptor) => FieldRenderer = 
		(field) => {
			switch (field) {
				case 'participant':
					return entry => entry.translation?.participant ? ({
						label: 'word.participant.singular',
						value: entry.translation.participant,
						descriptor: field,
						clickHandler: () => {
							this.activeParticipantService.activeParticipant = { id: entry.identifier.pid };
							this.router
								.navigateByUrl(participantPages.stammDaten.base())
								.catch(console.error);
						},
					}) : null;
				case 'workflow-item':
					return entry => entry.translation?.item ? ({
						label: 'word.workflow-item',
						value: entry.translation.item,
						descriptor: field,
						clickHandler: async () => {
							const workflow = await firstValueFrom(this.workflowService.workflow$);
							const item = workflow.items.find(item => item.id === entry.identifier.itemid);
							const url = this.workflowItemNavigationService.getFirstAvailableEntryURL(item);
							this.activeParticipantService.activeParticipant = { id: entry.identifier.pid };
							this.router
								.navigateByUrl(url)
								.catch(console.error);
						},
					}) : null;
			}
		};
}
